import { HomeGreeting } from '../../ui/HomeGreeting';
import { useCallback, useState, type ReactNode } from 'react';
import { usePageTab } from '../../shell/Shell';
import { sx } from '../../ui/designStyles';
import { useMemberAction, useMemberData, type MemberData } from './memberData';
import { Sheet, type SheetSpec } from './Sheet';
import { TodayTab } from './TodayTab';
import { MyHomeTab } from './MyHomeTab';
import { BenefitsTab } from './BenefitsTab';
import { EmergencyTab } from './EmergencyTab';
import { PeopleTab } from './PeopleTab';
import { Act, D, S } from './ui';
import { useFirstTalk } from '../companion/FirstTalk';
import type { QueryState } from '../../useQuery';
import type { CompanionFirstTalk } from '@aithora/shared';

// The member's Aithora Home (design T11043, hIsMember): five big tabs, a bottom sheet
// behind every tap, and one toast. The same component renders an Operator's read-only
// "view as member" (readOnly), so the two can never disagree.

export type MemberTab = 'today' | 'home' | 'money' | 'help' | 'people';

const TABS: [MemberTab, string][] = [['today', 'Today'], ['home', 'My home'], ['money', 'Benefits'], ['help', 'Emergency'], ['people', 'My people']];
const TABICON: Record<MemberTab, string> = {
  today: 'M12 7.4a4.6 4.6 0 1 0 0 9.2 4.6 4.6 0 0 0 0-9.2Z@M12 2.2v2.3@M12 19.5v2.3@M2.2 12h2.3@M19.5 12h2.3@M5.1 5.1l1.6 1.6@M17.3 17.3l1.6 1.6@M18.9 5.1l-1.6 1.6@M6.7 17.3l-1.6 1.6',
  home: 'M3 11.2 12 4l9 7.2@M5.4 9.7V20h13.2V9.7',
  money: 'M2.7 6.9h18.6v10.2H2.7Z@M12 9.7a2.3 2.3 0 1 0 0 4.6 2.3 2.3 0 0 0 0-4.6Z',
  help: 'M6.4 3.7h3.1l1.6 3.9-2 1.2a11 11 0 0 0 5 5l1.2-2 3.9 1.6v3.1a1.6 1.6 0 0 1-1.6 1.6A15.5 15.5 0 0 1 4.8 5.3a1.6 1.6 0 0 1 1.6-1.6Z',
  people: 'M9 11.4a3.4 3.4 0 1 0 0-6.8 3.4 3.4 0 0 0 0 6.8Z@M2.7 19.9a6.3 6.3 0 0 1 12.6 0@M16.8 5.3a3.4 3.4 0 0 1 0 6.5@M17.6 14.2a6.3 6.3 0 0 1 3.7 5.7',
};
const tabStyle = (on: boolean) =>
  'flex:0 0 auto;white-space:nowrap;display:inline-flex;flex-direction:column;align-items:center;justify-content:center;gap:3px;min-width:78px;min-height:64px;padding:7px 12px;border-radius:14px;font-size:16px;text-decoration:none;' +
  (on ? 'background:#2F5C97;color:#FCFAF4;font-weight:700;' : 'color:#3C454F;border-top:1px solid rgba(34,39,46,.14);font-weight:600;');

/** What every tab gets: the data, the sheet, the toast, and whether writes are allowed. */
export interface MemberCtx {
  personId: string;
  orgId: string | null;
  d: MemberData;
  readOnly: boolean;
  reason: string | null;
  openSheet: (s: SheetSpec) => void;
  setToast: (t: string | null) => void;
  goTab: (t: MemberTab) => void;
  /** Send a member action (PROPOSED POST /athome/me/:id/actions); resolves "done". */
  act: ReturnType<typeof useMemberAction>;
  /** The first talk (GET /residents/:id/onboarding), shown above Today until it is had. */
  firstTalk: QueryState<CompanionFirstTalk>;
  /** "Make the words bigger": the Home's text goes to its largest. */
  bigger: () => void;
}

/** The design's toast (hToast), placed by each tab where the design places it. */
export function Toast({ text, onClose }: { text: string | null; onClose: () => void }) {
  if (!text) return null;
  return (
    <D css="grid-column:1/-1;display:flex;align-items:center;gap:12px;flex-wrap:wrap;padding:9px 2px;border-radius:14px;background:rgba(47,138,111,.12);border:1px solid rgba(47,138,111,.4);animation:riseIn .3s cubic-bezier(.22,.61,.36,1);" role="status" testId="home-toast">
      <S css="flex:0 0 30px;display:inline-flex;align-items:center;justify-content:center;width:30px;height:30px;border-radius:9px;font-size:17px;font-weight:700;color:#FCFAF4;background:#1F6B52;">{'✓'}</S>
      <S css="flex:1 1 180px;min-width:0;font-size:17px;color:#12503C;line-height:1.4;">{text}</S>
      <Act css="flex:0 0 auto;display:inline-flex;align-items:center;min-height:44px;padding:0 18px;border-radius:12px;font-size:16px;color:#FCFAF4;background:#1F6B52;text-decoration:none;font-weight:700;" onClick={onClose}>
        Close
      </Act>
    </D>
  );
}

export function MemberHome({ personId, orgId, readOnly, reason, initialTab = 'today' }: { personId: string; orgId: string | null; readOnly: boolean; reason: string | null; initialTab?: MemberTab }) {
  const [tab, setTab] = useState<MemberTab>(initialTab);
  const [sheet, setSheet] = useState<SheetSpec | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const d = useMemberData(personId, orgId);
  const act = useMemberAction(personId);
  const firstTalk = useFirstTalk(personId);
  const [big, setBig] = useState(false);
  const zoom = big || firstTalk.data?.textSize === 'largest' ? 1.28 : 1;
  usePageTab(TABS.find((t) => t[0] === tab)?.[1] ?? 'Today');
  const goTab = useCallback((t: MemberTab) => {
    setTab(t);
    setToast(null);
  }, []);
  const ctx: MemberCtx = { personId, orgId, d, readOnly, reason, openSheet: setSheet, setToast, goTab, act, firstTalk, bigger: () => setBig(true) };
  const toastEl = <Toast text={toast} onClose={() => setToast(null)} />;
  let body: ReactNode = null;
  if (tab === 'today') body = <TodayTab c={ctx} toast={toastEl} />;
  else if (tab === 'home') body = <MyHomeTab c={ctx} toast={toastEl} />;
  else if (tab === 'money') body = <BenefitsTab c={ctx} />;
  else if (tab === 'help') body = <EmergencyTab c={ctx} />;
  else body = <PeopleTab c={ctx} toast={toastEl} />;

  return (
    <div style={sx('display:flex;flex-direction:column;min-width:0;flex:1 1 auto;min-height:0;height:100%;overflow:hidden;')} data-testid="member-home">
      <div style={sx(`zoom:${zoom};max-width:1080px;margin:0 auto;min-width:0;display:flex;flex-direction:column;gap:12px;border-top:1px solid rgba(34,39,46,.14);padding:clamp(10px,2.5vw,16px) clamp(12px,3.5vw,22px) clamp(12px,3vw,18px);flex:1 1 auto;min-height:0;overflow:hidden;`)}>
 {/* Aithora Home is the member's home: it opens on their greeting (Andrew, 2026-10-19). */}
        <HomeGreeting />
        <div role="tablist" aria-label="Aithora Home" style={sx('flex:0 0 auto;display:flex;gap:7px;min-width:0;overflow-x:auto;padding-bottom:2px;')}>
          {TABS.map(([k, label]) => {
            const on = tab === k;
            return (
              <a
                key={k}
                href="#"
                role="tab"
                aria-selected={on}
                style={sx(tabStyle(on))}
                onClick={(e) => {
                  e.preventDefault();
                  goTab(k);
                }}
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={on ? '#FCFAF4' : '#54606C'} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" style={sx('display:block;flex:0 0 22px;')} aria-hidden="true">
                  {TABICON[k].split('@').map((p) => (
                    <path key={p} d={p} />
                  ))}
                </svg>
                <span style={sx('display:block;')}>{label}</span>
                <span style={sx('display:block;width:26px;height:3px;border-radius:2px;margin-top:2px;background:' + (on ? 'rgba(252,250,244,.85)' : 'transparent') + ';')} />
              </a>
            );
          })}
        </div>
        <div style={sx('flex:1 1 auto;min-height:0;overflow-y:auto;overflow-x:hidden;min-width:0;display:block;padding-right:3px;')} role="tabpanel">
          {body}
        </div>
      </div>
      {sheet && (
        <Sheet
          sheet={sheet}
          readOnly={readOnly}
          reason={reason}
          onClose={(t) => {
            setSheet(null);
            if (t) setToast(t);
          }}
        />
      )}
    </div>
  );
}
